/**
 * The project's API tab (design "Flow API Tester"): try the project's services' APIs without fighting their sign-in.
 * On the left every service's endpoints (from its OpenAPI, else from its code) and History; on the right the request
 * (method, service, path, Params / Headers / Body, "Sign in as", Send ⌘↵) and its response (status, time, size, Body /
 * Headers / Logs / Data, Copy as curl). Calls are sent by vfs-service, next to the running service
 * (services/ApiTesterService.js): an app user's session stays on the server, a token the person gives stays in this
 * browser, and Flow's own sign-in is never sent to the app. A viewer sends GET only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ApiRail from './ApiRail';
import { RequestBar, RequestParts } from './RequestCard';
import ResponseCard, { SignInPrompt } from './ResponseCard';
import SignInDialog from './SignInDialog';
import useTabSelectionUrl from '../../hooks/useTabSelectionUrl';
import GrpcCard from './GrpcCard';
import { ErrorState, Loading, errorText } from '../data/DataCommon';
import { createTestUser, getAppUsers, getEndpoints, sendRequest, signIn } from '../../services/ApiTesterService';
import {
  addToHistory, clearHistory, curlFor, historyEntry, isReadMethod, readHistory, readToken, requestFor, saveToken, sendInput, shortResult,
} from '../../utils/apiTesterModel';
import '../data/Data.css';
import './ApiTester.css';

const blankRequest = (service) => ({ service, method: 'GET', path: '/', params: {}, query: [], headers: [], body: '', endpointKey: null });

const shortService = (name) => String(name || '').replace(/^services\//, '');
/** An endpoint as the URL names it (`&endpoint=GET /users/{id}`). */
const endpointName = (e) => `${e.method} ${e.path}`;

/**
 * The request a fresh API tab starts on: the service asked for (`wanted`, a link's `&service=`) on the endpoint asked
 * for (`endpoint`, `&endpoint=GET /users`) when it has it, else the first running service with endpoints, else the first
 * with endpoints.
 */
function defaultRequest(services, wanted = null, endpoint = null) {
  const asked = wanted ? services.find((s) => shortService(s.service) === wanted) : null;
  const first = asked || services.find((s) => s.running && s.endpoints.length) || services.find((s) => s.endpoints.length) || services[0];
  if (!first) { return null; }
  const named = asked && endpoint ? first.endpoints.find((e) => endpointName(e) === endpoint) : null;
  return first.endpoints.length ? requestFor(first.service, named || first.endpoints.find((e) => e.origin !== 'auth') || first.endpoints[0]) : blankRequest(first.service);
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function ApiPanel({ projectId, onOpenData, focus = null }) {
  const wanted = focus?.service ? shortService(focus.service) : null;
  const wantedEndpoint = wanted && focus?.endpoint ? String(focus.endpoint) : null;
  const [state, setState] = useState({ loading: true, error: null, services: [], canWrite: false });
  const [find, setFind] = useState('');
  const [history, setHistory] = useState(() => readHistory(projectId));
  const [request, setRequest] = useState(null);
  const [asValue, setAsValue] = useState('visitor');
  const [token, setToken] = useState(() => readToken(projectId));
  const [appUsers, setAppUsers] = useState({ auth: null, users: [], loading: false, error: null, service: null });
  const [answer, setAnswer] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [compare, setCompare] = useState({ rows: [], running: false, warning: null });

  const authService = useMemo(() => state.services.find((s) => s.auth) || null, [state.services]);
  const users = appUsers.users || [];
  const userOf = (value) => users.find((u) => `user:${u.id}` === value) || null;

  const load = useCallback(async() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const found = await getEndpoints(projectId);
      const services = found.services || [];
      setState({ loading: false, error: null, services, canWrite: found.canWrite !== false });
      // Kept across refreshes of the list, unless its service is gone.
      setRequest((current) => (current && services.some((s) => s.service === current.service) && !wanted ? current : defaultRequest(services, wanted, wantedEndpoint)));
    } catch (error) {
      setState((s) => ({ ...s, loading: false, error }));
    }
  }, [projectId, wanted, wantedEndpoint]);

  const loadUsers = useCallback(async() => {
    if (!authService) { return; }
    setAppUsers((u) => ({ ...u, auth: authService.auth, service: authService.service, loading: true, error: null }));
    try {
      const found = await getAppUsers(projectId, authService.service);
      setAppUsers((u) => ({ ...u, ...found, loading: false, error: null }));
    } catch (error) {
      setAppUsers((u) => ({ ...u, loading: false, error: errorText(error) }));
    }
  }, [authService, projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const endpoint = useMemo(() => {
    if (!request) { return null; }
    const service = state.services.find((s) => s.service === request.service);
    return service?.endpoints.find((e) => e.method === request.method && e.path === request.path) || null;
  }, [request, state.services]);

  // The endpoint in the URL (`?tab=api&service=<name>&endpoint=<METHOD /path>`) once the list is read; the one a fresh
  // tab starts on is not written, a request edited off the list keeps its service only.
  const start = useMemo(() => defaultRequest(state.services), [state.services]);
  const isStart = Boolean(start && request && endpoint && start.service === request.service && start.method === request.method && start.path === request.path);
  useTabSelectionUrl('api', {
    service: request && !isStart ? shortService(request.service) : null,
    endpoint: endpoint && !isStart ? endpointName(endpoint) : null,
  }, { ready: !state.loading && !state.error });

  const asPayload = (value = asValue) => {
    if (value === 'token') { return { kind: 'token', header: { name: token.name || 'Authorization', value: token.value } }; }
    const user = userOf(value);
    if (user) { return { kind: 'user', userId: user.id, service: authService?.service }; }
    return { kind: 'visitor' };
  };
  const asLabel = (value = asValue) => {
    const user = userOf(value);
    if (user) { return user.name || user.email; }
    return value === 'token' ? 'your header' : 'a visitor';
  };

  /** One call; `{ answer }` or `{ error }`; the person's own sends also go to History. */
  async function sendOne(value, { record = true, req = request } = {}) {
    const as = asPayload(value);
    try {
      const got = await sendRequest(projectId, { ...sendInput(req), as });
      if (record) { setHistory(addToHistory(projectId, historyEntry(req, { as: { ...as, label: asLabel(value) }, status: got.status, ms: got.ms }))); }
      return { answer: got };
    } catch (error) {
      if (record && error.code !== 'sign_in_needed') { setHistory(addToHistory(projectId, historyEntry(req, { as: { ...as, label: asLabel(value) }, error: errorText(error) }))); }
      return { error };
    }
  }

  async function send() {
    if (!request || sending) { return; }
    if (!state.canWrite && !isReadMethod(request.method)) { return; }
    setSending(true);
    setSendError(null);
    setPrompt(null);
    const { answer: got, error } = await sendOne(asValue);
    setSending(false);
    const user = userOf(asValue);
    if (error) {
      setAnswer(null);
      if (error.code === 'sign_in_needed' && user) { setPrompt({ user, busy: false, error: null }); return; }
      setSendError(errorText(error));
      return;
    }
    setAnswer(got);
    if (got.signedOut && user) { setPrompt({ user, busy: false, error: 'The app ended this session: sign in again' }); }
    if (got.as?.kind === 'user') { setAppUsers((u) => ({ ...u, users: u.users.map((x) => (x.id === got.as.userId ? { ...x, signedIn: true } : x)) })); }
  }

  async function signInAndSend(password) {
    setPrompt((p) => ({ ...p, busy: true, error: null }));
    try {
      await signIn(projectId, { service: authService.service, userId: prompt.user.id, password });
      setPrompt(null);
      await send();
    } catch (error) {
      setPrompt((p) => ({ ...p, busy: false, error: errorText(error) }));
    }
  }

  async function runCompare() {
    if (!request) { return; }
    const targets = [...users.filter((u) => !u.disabled).map((u) => ({ key: `user:${u.id}`, value: `user:${u.id}`, label: `${u.name || u.email}${u.roles.length ? ` (${u.roles.join(', ')})` : ''}` })), { key: 'visitor', value: 'visitor', label: 'Visitor' }];
    const warning = isReadMethod(request.method) ? null : `This sends ${request.method} ${targets.length} times, once for each.`;
    setCompare({ rows: targets.map((t) => ({ ...t })), running: true, warning });
    for (const target of targets) {
      const { answer: got, error } = await sendOne(target.value, { record: false });
      let row;
      if (error) { row = { status: null, error: errorText(error), result: error.code === 'sign_in_needed' ? 'needs sign-in (pick the user once)' : errorText(error).slice(0, 40) }; } else { row = { status: got.status, result: shortResult(got) }; }
      setCompare((c) => ({ ...c, rows: c.rows.map((r) => (r.key === target.key ? { ...r, ...row } : r)) }));
    }
    setCompare((c) => ({ ...c, running: false }));
  }

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      send();
    }
  };

  const pickAs = (value) => {
    if (value === 'manage') { setDialog(true); return; }
    if (value === 'user') { return; }
    setAsValue(value);
    setPrompt(null);
  };

  const changeToken = (next) => {
    setToken(next);
    saveToken(projectId, next);
  };

  const select = (service, picked) => {
    setRequest(requestFor(service.service, picked));
    setAnswer(null);
    setSendError(null);
    setPrompt(null);
  };

  // The bar's service picker: the request moves to that service, on its own version of the same call when it has one,
  // else on its first endpoint; the answer shown belonged to the other service.
  const changeService = (name) => {
    const target = state.services.find((s) => s.service === name);
    if (!target || name === request?.service) { return; }
    const same = target.endpoints.find((e) => e.method === request.method && e.path === request.path);
    const picked = same || target.endpoints.find((e) => e.origin !== 'auth') || target.endpoints[0];
    setRequest(picked ? requestFor(name, picked) : blankRequest(name));
    setAnswer(null);
    setSendError(null);
    setPrompt(null);
  };

  const pickHistory = (entry) => {
    setRequest({ service: entry.service, method: entry.method, path: entry.path, params: entry.params || {}, query: entry.query || [], headers: entry.headers || [], body: entry.body || '', endpointKey: `${entry.service} ${entry.method} ${entry.path}` });
    if (entry.as?.kind === 'token') { setAsValue('token'); } else if (entry.as?.kind === 'user' && users.some((u) => u.id === entry.as.userId)) { setAsValue(`user:${entry.as.userId}`); } else { setAsValue('visitor'); }
    setAnswer(null);
  };

  const copyCurl = () => {
    const service = state.services.find((s) => s.service === request?.service);
    const as = asPayload();
    return copyText(curlFor(request, { baseUrl: service?.previewUrl || null, sent: answer?.sent || null, as }));
  };

  if (state.loading && !state.services.length) { return <div className="api-panel"><Loading label="Finding the services' endpoints…" /></div>; }
  if (state.error) { return <div className="api-panel"><ErrorState error={errorText(state.error)} onRetry={load} /></div>; }
  if (!state.services.length || !request) {
    return <div className="api-panel"><p className="api-empty">This project has no services yet: once it has one, its endpoints show here.</p></div>;
  }

  const user = userOf(asValue);
  const addedNote = user ? `Headers Flow adds for you: the app session for ${user.name || user.email}${request.body ? ', Content-Type' : ''}.` : asValue === 'token' ? `Headers Flow adds for you: ${token.name || 'Authorization'} (yours).` : 'Sent as a visitor: no app sign-in.';

  return (
    // ⌘↵ anywhere in the tab sends.
    <div className="api-panel" data-testid="api-panel" onKeyDown={onKeyDown}>
      <ApiRail
        canStart={state.canWrite}
        find={find}
        history={history}
        onClearHistory={() => setHistory(clearHistory(projectId))}
        onFind={setFind}
        onPickHistory={pickHistory}
        onSelect={select}
        onStarted={load}
        projectId={projectId}
        selectedKey={request.endpointKey}
        services={state.services}
      />
      <main className="api-main">
        <RequestBar
          asValue={user || asValue === 'token' ? asValue : 'visitor'}
          canWrite={state.canWrite}
          onAsChange={pickAs}
          onChange={(patch) => {
            if (patch.service) { changeService(patch.service); return; }
            setRequest((r) => ({ ...r, ...patch, endpointKey: patch.method || patch.path ? null : r.endpointKey }));
          }}
          onSend={send}
          onStarted={load}
          onTokenChange={changeToken}
          projectId={projectId}
          request={request}
          sending={sending}
          services={state.services}
          token={token}
          users={users}
        />
        <div className="api-split">
          <RequestParts addedNote={addedNote} endpoint={endpoint} key={request.endpointKey || 'free'} onChange={(patch) => setRequest((r) => ({ ...r, ...patch }))} request={request} />
          <ResponseCard
            answer={answer}
            error={sendError}
            onCopyCurl={copyCurl}
            onOpenData={onOpenData}
            sending={sending}
            signIn={prompt ? <SignInPrompt busy={prompt.busy} error={prompt.error} onCancel={() => setPrompt(null)} onSignIn={signInAndSend} user={prompt.user} /> : null}
          />
        </div>
        <GrpcCard canWrite={state.canWrite} projectId={projectId} service={request.service} />
      </main>
      {dialog && (
        <SignInDialog
          appUsers={appUsers}
          asValue={asValue}
          canWrite={state.canWrite}
          compare={compare}
          onClose={() => setDialog(false)}
          onCompare={runCompare}
          onCreateTestUser={async(input) => {
            const made = await createTestUser(projectId, { service: authService.service, ...input });
            await loadUsers();
            setAppUsers((u) => ({ ...u, note: made.note || null }));
            setAsValue(`user:${made.user.id}`);
          }}
          onPick={pickAs}
          request={request}
        />
      )}
    </div>
  );
}

ApiPanel.propTypes = {
  focus: PropTypes.shape({ service: PropTypes.string, endpoint: PropTypes.string }),
  onOpenData: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};

